import React, { useState } from 'react'
import Filter from 'src/components/search/Filter'
import Button from 'src/components/ui/Button'
import type { SearchSort } from '@faststore/sdk/dist/types'
import type {
  Filter_FacetsFragment,
  IStoreSelectedFacet,
  Scalars,
} from '@generated/graphql'
import Sort from 'src/components/search/Sort'
import { Skeleton } from '@acctglobal/skeleton'
import { useHeaderContext } from 'src/contexts/header-context'
import { useSearch } from '@faststore/sdk'
import useWindowDimensions from 'src/hooks/useWindowDimensions'

import useFilterFacets from '../../search/Filter/hooks/useFilterFacets'
import { facetsFilteredFormatted } from '../../search/Filter/utils'
import getCmsFilterConfig from './useCmsFilterConfig'
import PlusFilterIcon from './svg/plus-filter-icon'
import LayoutGridList from './LayoutGridList'

import './product-gallery.scss'

interface AnchorOffset {
  top: number
}

interface ProductGalleryAboveProps {
  anchorOffset: AnchorOffset | undefined
  stickyClass: string
  isFilterOpen: boolean
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  filteredFacets: Filter_FacetsFragment[]
  term: string | null
  base: string
  layout: string
  handleChangeLayout: (value: string) => void
  isSearchPage: boolean
  totalCount: number
  defaultFacets?: Array<{
    key: string
    value: string
  }>
  selectedFacets: IStoreSelectedFacet[]
  setSelectedFacets: React.Dispatch<React.SetStateAction<IStoreSelectedFacet[]>>
  sort: SearchSort
}

interface HighlightsType {
  firstFilter: string
  secondFilter: string
  thirdFilter: string
}

interface FacetsObjectType {
  key: string
  label: string
  min?: Max
  max?: Max
  __typename: string
  values?: Value[]
  value?: Scalars['String']
}

interface Max {
  selected: number
  absolute: number
}

interface Value {
  label: string
  value: string
  selected: boolean
  quantity: number
}

interface SelectedFacetSimpleType {
  key: string
  value: string
}

const sortFilters = (
  highlights: HighlightsType,
  allElements: FacetsObjectType[]
) => {
  const destaqueValores = Object.values(highlights)

  return allElements.sort((a: { label: string }, b: { label: string }) => {
    if (
      destaqueValores.includes(a.label) &&
      !destaqueValores.includes(b.label)
    ) {
      return -1
    }

    if (
      !destaqueValores.includes(a.label) &&
      destaqueValores.includes(b.label)
    ) {
      return 1
    }

    return 0
  })
}

const verifyIsSelected = (
  selectedFacets: SelectedFacetSimpleType[],
  arg: { key: string }
) => {
  return selectedFacets.find((item: SelectedFacetSimpleType) => {
    if (item.key === arg.key) {
      return true
    }

    return false
  })
}

export const ProductGalleryAbove = ({
  anchorOffset,
  stickyClass,
  isFilterOpen,
  setIsFilterOpen,
  filteredFacets,
  term,
  base,
  layout,
  handleChangeLayout,
  isSearchPage,
  totalCount,
  defaultFacets,
  selectedFacets,
  setSelectedFacets,
  sort,
}: ProductGalleryAboveProps) => {
  const { isMenuOpen } = useHeaderContext()
  const searchState = useSearch()
  const { isMobile, width, height } = useWindowDimensions()
  const content = getCmsFilterConfig()
  const heightNotNull = height ?? window.innerHeight
  const isTablet = !!(
    width <= 1024 &&
    width > 431 &&
    heightNotNull <= 1366 &&
    heightNotNull > 933
  )

  const [filtersExpanded, setFiltersExpanded] = useState<IStoreSelectedFacet>()
  const facetsFiltered = useFilterFacets({
    facets: filteredFacets,
    selectedFacetsFilter: selectedFacets,
    filterByUrl: true,
    term,
    base,
  })

  const facets =
    facetsFiltered?.filter(({ key }) => key?.indexOf('category') === -1) ?? []

  const facetsFormatted = facetsFilteredFormatted(facets)
  const ordernedFilters = (
    content ? sortFilters(content?.[0]?.data, facetsFormatted) : facetsFormatted
  ).slice(0, 3)

  return (
    <>
      <div
        id="productgrid"
        className="product-grid-anchor"
        style={anchorOffset}
      />
      <div
        className={`product-listing__filters${stickyClass} ${
          isMenuOpen ? 'menu-open' : ''
        }`}
      >
        <Filter
          isOpen={isFilterOpen}
          setIsOpen={setIsFilterOpen}
          facets={filteredFacets}
          onDismiss={() => setIsFilterOpen(false)}
          term={term}
          base={base}
          searchState={searchState}
          defaultFacets={defaultFacets}
          selectedFacets={selectedFacets}
          setSelectedFacets={setSelectedFacets}
          sort={sort}
          filtersExpanded={filtersExpanded}
        />
      </div>

      <div className={`product-listing__wrapper${isMobile ? stickyClass : ''}`}>
        <div className="product-listing__sort">
          <div className="product-listing__sort-col">
            {isMobile || isTablet ? (
              <div className="product-listing__list-filters">
                {ordernedFilters.map((arg: any, index: number) => (
                  <button
                    key={index}
                    className={`product-listing__highlight-filters${
                      verifyIsSelected(selectedFacets, arg) ? ' selected' : ''
                    }`}
                    onClick={() => {
                      setIsFilterOpen(true)
                      setFiltersExpanded(arg)
                    }}
                  >
                    {arg.label}
                  </button>
                ))}
              </div>
            ) : (
              <></>
            )}
            <Button
              variant="tertiary"
              data-testid="open-filter-button"
              icon={<PlusFilterIcon />}
              iconPosition="left"
              aria-label="Abrir filtros"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              FILTROS
            </Button>
          </div>
          {!isMobile || heightNotNull >= 1023 ? <Sort /> : <></>}
        </div>

        <div className="product-listing__results-count" data-count={totalCount}>
          {isMobile && heightNotNull <= 1023 ? (
            <Sort />
          ) : (
            <LayoutGridList
              layout={layout}
              handleChangeLayout={handleChangeLayout}
            />
          )}

          {isSearchPage ? (
            totalCount !== 0 ? (
              <h2
                data-testid="total-product-count"
                className="total-product-count"
              >
                ENCONTRAMOS <strong>{totalCount} RESULTADOS</strong> PARA SUA
                BUSCA
              </h2>
            ) : (
              <Skeleton width={325} height={16} backgroundColor="#F4F4F4" />
            )
          ) : totalCount !== 0 ? (
            <h2
              data-testid="total-product-count"
              className="total-product-count"
            >
              <strong>{totalCount} Produtos</strong>
            </h2>
          ) : (
            <Skeleton width={90} height={16} backgroundColor="#F4F4F4" />
          )}
        </div>
      </div>
    </>
  )
}
